/**
 * CountryPanel — the "Clan Hub" for a player's homeland.
 *
 * Auto-created server-side the first time any player picks that homeland.
 * The first picker is the country's Leader (edit description + declare war).
 * Members share a Treasury; anyone can donate. Wars are damage-pool events —
 * either side rallies on a 60s cooldown; window is 30 min; higher pool wins
 * and, if it's the attacker, the region ownership flips.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Flag, Coins, Crown, Users, Trophy, Swords, ShieldAlert, Clock, X, Zap,
  Send, MessageCircle, Landmark, Sword, Palette, Flame,
} from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { flagUrl } from "@/lib/utils";
import { useGame } from "@/context/GameContext";
import { useOpenPlayer } from "@/components/game/PlayerCard";

// -----------------------------------------------------------------------

function StatBlock({ label, value, testid }) {
  return (
    <div
      className="panel-inset px-3 py-2 flex-1 min-w-[92px] flex flex-col items-start"
      data-testid={testid}
    >
      <span className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">{label}</span>
      <span className="font-numeric text-xl sm:text-2xl text-amber-300 leading-none mt-1">
        {(value ?? 0).toLocaleString()}
      </span>
    </div>
  );
}

function CountdownPill({ endsAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const end = new Date(endsAt).getTime();
  const remaining = Math.max(0, end - now);
  const mm = Math.floor(remaining / 60000);
  const ss = Math.floor((remaining % 60000) / 1000);
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-numeric text-amber-300 bg-stone-950 border border-amber-800 rounded px-1.5 py-0.5">
      <Clock size={11} /> {mm.toString().padStart(2, "0")}:{ss.toString().padStart(2, "0")}
    </span>
  );
}

// -----------------------------------------------------------------------

function DonateDialog({ open, onClose, maxGold, onDonated }) {
  const [amount, setAmount] = useState(100);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setAmount(Math.min(100, maxGold)); }, [open, maxGold]);
  if (!open) return null;

  const submit = async () => {
    const amt = Math.max(1, Math.min(Number(amount) || 0, maxGold));
    if (amt < 1) { toast.error("Donate at least 1 gp."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/api/game/country/donate", { amount: amt });
      toast.success(`Donated ${amt.toLocaleString()} gp`, {
        description: `Treasury now holds ${data.treasury_gold.toLocaleString()} gp`,
      });
      onDonated?.(data);
      onClose();
    } catch (e) { toast.error("Donation failed", { description: formatApiError(e) }); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="country-donate-modal"
    >
      <div className="panel w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg heading-gilded flex items-center gap-2">
            <Coins size={16} className="text-amber-400" /> Donate to Treasury
          </h3>
          <button onClick={onClose} className="text-stone-500 hover:text-amber-300"><X size={18} /></button>
        </div>
        <div className="text-xs font-body text-stone-400 mb-2">
          Contribute gold to fund national upgrades and war declarations. You currently hold {(maxGold ?? 0).toLocaleString()} gp.
        </div>
        <input
          type="number"
          min={1}
          max={maxGold}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 text-lg font-numeric text-amber-300 focus:border-amber-600 outline-none"
          data-testid="country-donate-input"
        />
        <div className="flex gap-1.5 mt-2">
          {[100, 1000, 10000, "MAX"].map((v) => (
            <button
              key={v}
              onClick={() => setAmount(v === "MAX" ? maxGold : Math.min(v, maxGold))}
              className="flex-1 text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-400 hover:text-amber-300 hover:border-amber-800 transition-colors"
            >
              {typeof v === "number" ? `+${v.toLocaleString()}` : "MAX"}
            </button>
          ))}
        </div>
        <button
          onClick={submit}
          disabled={busy || !maxGold}
          className="w-full mt-4 btn-gold px-4 py-2 flex items-center justify-center gap-2 font-body"
          data-testid="country-donate-submit"
        >
          <Send size={14} /> {busy ? "Sending…" : "Donate"}
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------

function DeclareWarDialog({ open, onClose, treasury, warCfg, onDeclared }) {
  const [regionId, setRegionId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setRegionId(""); }, [open]);
  if (!open) return null;

  const submit = async () => {
    if (!regionId.trim()) { toast.error("Enter a region id (e.g. USA-1)."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/api/game/country/war/declare", { region_id: regionId.trim().toUpperCase() });
      toast.success("War declared", { description: `${data.war.region_name} · window ${warCfg.duration_min}m` });
      onDeclared?.(data);
      onClose();
    } catch (e) { toast.error("Declaration failed", { description: formatApiError(e) }); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="country-war-modal"
    >
      <div className="panel w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg heading-gilded flex items-center gap-2">
            <Swords size={16} className="text-red-400" /> Declare War
          </h3>
          <button onClick={onClose} className="text-stone-500 hover:text-amber-300"><X size={18} /></button>
        </div>
        <div className="text-xs font-body text-stone-400 mb-3">
          Spend Treasury to challenge an enemy region. Cost is <span className="text-amber-300">{warCfg.cost_per_weight} gp × region weight</span>.
          Battle window <span className="text-amber-300">{warCfg.duration_min} min</span>. You have <span className="text-amber-300">{treasury.toLocaleString()} gp</span> in treasury.
        </div>
        <label className="block text-[10px] uppercase tracking-widest font-numeric text-stone-500 mb-1">Region ID</label>
        <input
          value={regionId}
          onChange={(e) => setRegionId(e.target.value.toUpperCase())}
          placeholder="e.g. FRA-1"
          className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 font-numeric text-amber-300 uppercase tracking-widest focus:border-amber-600 outline-none"
          data-testid="country-war-region"
        />
        <p className="text-[11px] font-body text-stone-500 mt-2">
          Tip: open the World Map, hover any region to reveal its ID (e.g. <span className="font-numeric text-amber-400">AUS-3</span>).
        </p>
        <button
          onClick={submit}
          disabled={busy || treasury < warCfg.min_treasury}
          className="w-full mt-4 px-4 py-2 rounded font-body flex items-center justify-center gap-2 transition-colors bg-red-800 hover:bg-red-700 text-stone-100 disabled:bg-stone-800 disabled:text-stone-500"
          data-testid="country-war-submit"
        >
          <Swords size={14} /> {busy ? "Marshalling banners…" : "Declare War"}
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------

function WarCard({ war, myIso, onAttack }) {
  const [busy, setBusy] = useState(false);
  const myDmg  = war.attacker_iso === myIso ? war.damage.attacker : war.damage.defender;
  const foeDmg = war.attacker_iso === myIso ? war.damage.defender : war.damage.attacker;
  const total  = Math.max(1, myDmg + foeDmg);
  const myPct  = Math.round((myDmg / total) * 100);
  const iAmAttacker = war.attacker_iso === myIso;
  const foeIso = iAmAttacker ? war.defender_iso : war.attacker_iso;

  const rally = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/game/country/war/${war.id}/attack`);
      toast.success(`Rally +${data.damage} damage`);
      onAttack?.(data.war);
    } catch (e) { toast.error("Rally failed", { description: formatApiError(e) }); }
    finally { setBusy(false); }
  };

  return (
    <div className="panel-inset p-3" data-testid={`war-${war.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Swords size={14} className="text-red-400 shrink-0" />
          <span className="font-body text-sm text-stone-200 truncate">
            {war.region_name} <span className="font-numeric text-[11px] text-stone-500 ml-1">/{war.region_id}/</span>
          </span>
        </div>
        <CountdownPill endsAt={war.ends_at} />
      </div>

      <div className="flex items-center gap-2 text-[11px] font-body">
        <div className="flex items-center gap-1.5">
          <img src={flagUrl(myIso.slice(0, 2))} alt={myIso} className="w-4 h-3 object-cover rounded-[2px]" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <span className="text-emerald-300">{iAmAttacker ? "You attack" : "You defend"}</span>
        </div>
        <span className="text-stone-600">vs</span>
        <div className="flex items-center gap-1.5">
          {foeIso && (
            <img src={flagUrl(foeIso.slice(0, 2))} alt={foeIso} className="w-4 h-3 object-cover rounded-[2px]" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          )}
          <span className="text-red-300">{foeIso || war.region_country_iso}</span>
        </div>
      </div>

      <div className="mt-2 h-2 rounded bg-stone-900 overflow-hidden flex">
        <div className="bg-emerald-600" style={{ width: `${myPct}%` }} />
        <div className="bg-red-700 flex-1" />
      </div>
      <div className="flex justify-between text-[11px] font-numeric mt-1">
        <span className="text-emerald-300">{myDmg.toLocaleString()}</span>
        <span className="text-red-300">{foeDmg.toLocaleString()}</span>
      </div>

      <button
        onClick={rally}
        disabled={busy}
        className="w-full mt-3 flex items-center justify-center gap-1 text-xs font-body py-1.5 rounded border border-amber-800 text-amber-300 hover:bg-amber-950/30 transition-colors"
        data-testid={`war-${war.id}-rally`}
      >
        <Zap size={12} /> {busy ? "Rallying…" : "Rally for damage"}
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------

function WildernessRegions({ regions, onToggle, busy }) {
  if (!regions?.length) {
    return (
      <div className="text-stone-500 italic font-body text-sm py-6 text-center">
        Your country holds no regions yet.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[380px] overflow-y-auto pr-1">
      {regions.map((r) => (
        <div
          key={r.region_id}
          className={
            "flex items-center gap-2 rounded border p-2 " +
            (r.wilderness
              ? "border-red-800 bg-red-950/20"
              : "border-stone-800 bg-stone-950/40")
          }
          data-testid={`country-wild-${r.region_id}`}
        >
          <div className={
            "w-8 h-8 rounded flex items-center justify-center shrink-0 " +
            (r.wilderness ? "bg-red-950/60 border border-red-700" : "bg-stone-900 border border-stone-700")
          }>
            <Flame size={13} className={r.wilderness ? "text-red-400" : "text-stone-600"} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-body text-sm text-stone-200 truncate">{r.region_name}</div>
            <div className="text-[10px] font-numeric text-stone-500">
              /{r.region_id}/ · weight {r.weight}
            </div>
          </div>
          <button
            onClick={() => onToggle(r.region_id, !r.wilderness)}
            disabled={busy}
            className={
              "text-[10px] font-body px-2 py-1 rounded transition-colors disabled:opacity-50 " +
              (r.wilderness
                ? "border border-red-700 text-red-200 bg-red-950/40 hover:bg-red-950/60"
                : "border border-stone-700 text-stone-300 hover:text-red-300 hover:border-red-800")
            }
            data-testid={`country-wild-toggle-${r.region_id}`}
          >
            {r.wilderness ? "Unflag" : "Flag Wild"}
          </button>
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------

export function CountryPanel() {
  const { snap } = useGame();
  const openPlayer = useOpenPlayer();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDesc, setEditDesc] = useState(false);
  const [desc, setDesc] = useState("");
  const [donateOpen, setDonateOpen] = useState(false);
  const [warOpen, setWarOpen] = useState(false);
  const [colorBusy, setColorBusy] = useState(false);

  // Debounce colour picks so we don't hit the API on every mouse-drag frame.
  const setColor = useCallback((hex) => {
    setData((d) => (d ? { ...d, country: { ...d.country, map_color: hex } } : d));
    if (setColor._t) clearTimeout(setColor._t);
    setColor._t = setTimeout(async () => {
      setColorBusy(true);
      try {
        await api.post("/api/game/country/color", { color: hex });
        toast.success(`Map colour set to ${hex}`);
      } catch (e) { toast.error("Colour update failed", { description: formatApiError(e) }); }
      finally { setColorBusy(false); }
    }, 350);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/country/mine");
      setData(data);
      setDesc(data.country.description || "");
    } catch (e) { toast.error("Country not available", { description: formatApiError(e) }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Live-refresh wars while any is active so the countdown + damage stays fresh.
  useEffect(() => {
    if (!data?.active_wars?.length) return undefined;
    const iv = setInterval(load, 6000);
    return () => clearInterval(iv);
  }, [data?.active_wars?.length, load]);

  const iso2 = data?.country?.iso_a2 || snap?.home_country_iso_a2;
  const iso3 = data?.country?.iso_a3;

  const saveDesc = async () => {
    try {
      await api.post("/api/game/country/description", { description: desc });
      toast.success("Country description updated.");
      setEditDesc(false);
      load();
    } catch (e) { toast.error("Save failed", { description: formatApiError(e) }); }
  };

  const stats = data?.world_stats;
  const myGold = snap?.gold ?? 0;
  const membersSorted = useMemo(() => data?.members || [], [data]);

  const [wildBusy, setWildBusy] = useState(false);
  const toggleWild = async (region_id, wilderness) => {
    setWildBusy(true);
    try {
      await api.post("/api/game/wilderness/toggle", { region_id, wilderness });
      toast.success(wilderness ? `${region_id} declared Wilderness.` : `${region_id} restored to peace.`);
      // Optimistic local update
      setData((d) => d ? {
        ...d,
        held_regions: (d.held_regions || []).map((r) =>
          r.region_id === region_id ? { ...r, wilderness } : r
        ),
      } : d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWildBusy(false); }
  };

  if (loading && !data) return <div className="p-6 text-stone-500 italic">Reading the annals…</div>;
  if (!data) return <div className="p-6 text-stone-500 italic">No country yet — pick a homeland from the World Map first.</div>;

  const c = data.country;
  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="country-panel">
      {/* Header — world stats block matches the app typography */}
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-numeric text-amber-500 mb-1">
            <Flag size={11} className="text-amber-500" /> Chapter · The Homeland
          </div>
          <h2 className="font-display text-2xl sm:text-3xl heading-gilded">
            {c.name}
          </h2>
        </div>
        <div className="flex gap-2">
          <StatBlock label="Nations" value={stats?.nations} testid="stat-nations" />
          <StatBlock label="Regions" value={stats?.regions} testid="stat-regions" />
          <StatBlock label="Held"    value={stats?.held}    testid="stat-held" />
        </div>
      </div>

      {/* Identity band */}
      <div className="panel overflow-hidden">
        <div className="p-4 sm:p-6 flex items-start gap-3 sm:gap-6">
          {iso2 && (
            <img
              src={flagUrl(iso2, 160)}
              alt={iso3}
              className="w-16 h-11 sm:w-28 sm:h-20 object-cover rounded shadow-[0_0_0_1px_rgba(0,0,0,0.6)]"
              data-testid="country-flag-large"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl sm:text-4xl heading-gilded truncate">{c.name}</h1>
              <span className="text-[10px] font-numeric uppercase tracking-widest text-amber-300 bg-stone-950 border border-amber-800/60 rounded-full px-2 py-0.5">
                {c.iso_a3}
              </span>
              {data.rank && (
                <span className="text-[10px] font-numeric uppercase tracking-widest text-emerald-300 bg-stone-950 border border-emerald-800/60 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                  <Trophy size={10} /> Rank #{data.rank}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs sm:text-sm font-numeric text-stone-300">
              <span className="inline-flex items-center gap-1">
                <Crown size={11} className="text-amber-500" /> Leader ·
                <button
                  onClick={() => openPlayer(c.leader_id)}
                  className="ml-1 text-amber-300 hover:underline underline-offset-2"
                  data-testid="country-leader-name"
                >
                  {c.leader_name}
                </button>
              </span>
              <span className="inline-flex items-center gap-1">
                <Users size={11} /> {data.member_count} member{data.member_count === 1 ? "" : "s"}
              </span>
              <span className="inline-flex items-center gap-1">
                <Landmark size={11} /> {stats?.held} regions held
              </span>
            </div>
            {editDesc ? (
              <div className="mt-2 space-y-2">
                <textarea
                  value={desc}
                  maxLength={240}
                  onChange={(e) => setDesc(e.target.value)}
                  rows={3}
                  className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1.5 text-sm font-body focus:border-amber-600 outline-none"
                  data-testid="country-desc-input"
                />
                <div className="flex justify-end gap-2">
                  <button onClick={() => { setDesc(c.description); setEditDesc(false); }} className="btn-ghost-gold text-xs px-3 py-1">Cancel</button>
                  <button onClick={saveDesc} className="btn-gold text-xs px-3 py-1" data-testid="country-desc-save">Save</button>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-sm text-stone-300 font-body italic" data-testid="country-desc">
                {c.description || "This nation's story is unwritten."}
                {data.is_leader && (
                  <button
                    onClick={() => setEditDesc(true)}
                    className="ml-2 text-[11px] font-body text-stone-500 hover:text-amber-400 underline underline-offset-2"
                    data-testid="country-desc-edit"
                  >
                    edit
                  </button>
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Treasury + Wars row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Treasury card */}
        <section className="panel p-4 space-y-3">
          <header className="flex items-center gap-2">
            <Coins size={14} className="text-amber-400" />
            <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 flex-1">Treasury</h3>
          </header>
          <div className="font-numeric text-4xl text-amber-300 leading-none" data-testid="country-treasury">
            {c.treasury_gold.toLocaleString()}
          </div>
          <div className="text-[11px] font-body text-stone-500">
            Shared war chest. Funds national upgrades and war declarations.
          </div>
          <button
            onClick={() => setDonateOpen(true)}
            disabled={myGold <= 0}
            className="w-full btn-gold px-4 py-2 flex items-center justify-center gap-2 font-body"
            data-testid="country-donate-open"
          >
            <Send size={14} /> Donate ({myGold.toLocaleString()} gp)
          </button>
          {data.is_leader && (
            <button
              onClick={() => setWarOpen(true)}
              disabled={c.treasury_gold < (data.war_config?.min_treasury ?? 500)}
              className="w-full flex items-center justify-center gap-2 text-xs font-body px-4 py-2 rounded border border-red-800 text-red-300 hover:bg-red-950/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="country-declare-open"
            >
              <Swords size={13} /> Declare War
            </button>
          )}

          {/* Map colour — leader only, everyone else just sees the swatch */}
          <div className="pt-2 border-t border-stone-800">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-numeric text-amber-500 mb-2">
              <Palette size={11} className="text-amber-400" /> Map colour
            </div>
            <div className="flex items-center gap-2">
              <span
                className="w-8 h-8 rounded border border-stone-700 shrink-0"
                style={{ background: c.map_color || "#c94a2c" }}
                data-testid="country-color-swatch"
              />
              {data.is_leader ? (
                <>
                  <input
                    type="color"
                    value={c.map_color || "#c94a2c"}
                    onChange={(e) => setColor(e.target.value)}
                    className="flex-1 h-8 rounded border border-stone-700 bg-stone-950 cursor-pointer"
                    data-testid="country-color-picker"
                    disabled={colorBusy}
                  />
                  <span className="text-[10px] font-numeric text-stone-500 uppercase tracking-widest">
                    {c.map_color || "auto"}
                  </span>
                </>
              ) : (
                <>
                  <span className="flex-1 text-xs font-body text-stone-400">
                    Chosen by the Leader.
                  </span>
                  <span className="text-[10px] font-numeric text-stone-500 uppercase tracking-widest">
                    {c.map_color || "auto"}
                  </span>
                </>
              )}
            </div>
            {data.is_leader && (
              <p className="text-[10px] font-body text-stone-500 mt-1.5">
                Painted onto every region {c.iso_a3} holds — live for every player.
              </p>
            )}
          </div>
        </section>

        {/* Wars column (spans 2) */}
        <section className="lg:col-span-2 space-y-3">
          <div className="panel p-4">
            <header className="flex items-center gap-2 mb-2">
              <ShieldAlert size={14} className="text-red-400" />
              <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 flex-1">Active Wars</h3>
              <span className="text-[10px] font-numeric text-stone-500">{data.active_wars.length}</span>
            </header>
            {data.active_wars.length === 0 ? (
              <div className="text-stone-500 italic font-body text-sm py-6 text-center">
                No wars right now. The realm holds its breath.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.active_wars.map((w) => (
                  <WarCard
                    key={w.id}
                    war={w}
                    myIso={c.iso_a3}
                    onAttack={(updated) => setData((d) => ({
                      ...d,
                      active_wars: d.active_wars.map((x) => x.id === updated.id ? updated : x),
                    }))}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Wilderness regions — leader only */}
          {data.is_leader && (
            <div className="panel p-4" data-testid="country-wilderness-manager">
              <header className="flex items-center gap-2 mb-2">
                <Flame size={14} className="text-red-400" />
                <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 flex-1">
                  Wilderness Regions
                </h3>
                <span className="text-[10px] font-numeric text-stone-500">
                  {(data.held_regions || []).filter((r) => r.wilderness).length} / {(data.held_regions || []).length} flagged
                </span>
              </header>
              <p className="text-[11px] font-body text-stone-500 mb-3 italic">
                Flag any region your country holds as Wilderness. Players who walk in are auto-flagged for PvP:
                target range ±10 CL, death drops 10% of carried gold + up to 3 items.
              </p>
              <WildernessRegions
                regions={data.held_regions || []}
                onToggle={toggleWild}
                busy={wildBusy}
              />
            </div>
          )}

          {data.recent_wars.length > 0 && (
            <div className="panel p-4">
              <header className="flex items-center gap-2 mb-2">
                <Sword size={14} className="text-stone-500" />
                <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 flex-1">Recent Wars</h3>
              </header>
              <div className="space-y-1">
                {data.recent_wars.map((w) => {
                  const won = w.winner_iso === c.iso_a3;
                  const iAmAttacker = w.attacker_iso === c.iso_a3;
                  return (
                    <div key={w.id} className="flex items-center justify-between text-xs font-body py-1" data-testid={`recent-war-${w.id}`}>
                      <span className="text-stone-300 truncate">
                        {iAmAttacker ? "Attacked" : "Defended"} <span className="text-amber-300">{w.region_name}</span> <span className="text-stone-600 font-numeric">/{w.region_id}/</span>
                      </span>
                      <span className={"font-numeric uppercase tracking-widest text-[10px] " + (won ? "text-emerald-300" : "text-red-400")}>
                        {won ? "Victory" : "Defeat"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Members */}
      <div className="panel p-4">
        <header className="flex items-center gap-2 mb-3">
          <Users size={14} className="text-amber-400" />
          <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 flex-1">Members</h3>
          <span className="text-[10px] font-numeric text-stone-500">{data.member_count}</span>
        </header>
        {membersSorted.length === 0 ? (
          <div className="text-stone-500 italic font-body text-sm py-4 text-center">
            You are the first to raise this banner.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {membersSorted.slice(0, 30).map((m) => (
              <button
                key={m.id}
                onClick={() => openPlayer(m.id)}
                className="panel-inset px-3 py-2 flex items-center gap-2 text-left hover:border-amber-800 transition-colors"
                data-testid={`country-member-${m.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-stone-950 border border-amber-800 flex items-center justify-center font-display text-[10px] text-amber-300 shrink-0">
                  {(m.name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-body text-sm text-stone-200 truncate flex items-center gap-1">
                    {m.name}
                    {m.id === c.leader_id && (
                      <Crown size={10} className="text-amber-400 shrink-0" />
                    )}
                  </div>
                  <div className="text-[10px] font-numeric text-stone-500">
                    Total {m.total_level} · Combat {m.combat_level}
                  </div>
                </div>
                <span className={
                  "text-[9px] font-numeric uppercase tracking-widest shrink-0 " +
                  (m.status === "In combat" ? "text-red-400"
                    : m.status === "Skilling" ? "text-emerald-400" : "text-stone-500")
                }>
                  {m.status}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* World top-10 */}
      <div className="panel p-4">
        <header className="flex items-center gap-2 mb-3">
          <Trophy size={14} className="text-amber-400" />
          <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 flex-1">Nations Standing</h3>
        </header>
        <ol className="space-y-1">
          {data.top_countries.map((row, i) => {
            const mine = row.iso_a3 === c.iso_a3;
            const iso2 = (row.iso_a3 || "").slice(0, 2);
            return (
              <li
                key={row.iso_a3}
                className={
                  "flex items-center gap-3 px-2 py-1.5 rounded border " +
                  (mine ? "bg-amber-950/40 border-amber-700" : "border-transparent hover:bg-stone-900/50")
                }
                data-testid={`world-rank-${i}`}
              >
                <span className="font-numeric text-sm w-8 text-right text-stone-400">#{row.rank}</span>
                <img
                  src={flagUrl(iso2)}
                  alt={row.iso_a3}
                  className="w-5 h-3.5 object-cover rounded-[2px]"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <span className="flex-1 font-body text-sm text-stone-200 truncate">{row.name}</span>
                <span className="text-[11px] font-numeric text-stone-500">{row.member_count} m</span>
                <span className="text-[11px] font-numeric text-amber-300 w-16 text-right">{row.held} held</span>
                <span className="text-[11px] font-numeric text-amber-300 w-24 text-right">{row.treasury_gold.toLocaleString()} gp</span>
              </li>
            );
          })}
        </ol>
      </div>

      <DonateDialog
        open={donateOpen}
        onClose={() => setDonateOpen(false)}
        maxGold={myGold}
        onDonated={() => load()}
      />
      <DeclareWarDialog
        open={warOpen}
        onClose={() => setWarOpen(false)}
        treasury={c.treasury_gold}
        warCfg={data.war_config}
        onDeclared={() => load()}
      />
    </div>
  );
}
